import { useState, useMemo } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Sidebar Component
 * Left navigation sidebar with Start Designing button and menu items
 * Matches the DX LIVING design
 */

const Sidebar = () => {
  const { currentView, setCurrentView, setRoomId, setRoomImageId, setPhotorealisticImage } = useSidebar();
  const { user } = useAuth();
  const [activeItem, setActiveItem] = useState('Dashboard');
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  /** Narrow rail (w-20): stack icon + label vertically so text stays visible. */
  const narrowLabelClass =
    'block w-full text-center text-[10px] font-medium leading-tight text-balance px-0.5';

  const isSupplier = useMemo(
    () => (user?.userRoles ?? []).some((r) => String(r).toLowerCase() === 'supplier'),
    [user?.userRoles]
  );

  const menuItems = useMemo(() => {
    const items = [
      { id: 'Dashboard', label: 'Dashboard', icon: 'grid' },
      { id: 'Projects', label: 'Projects', icon: 'ruler' },
      // { id: 'AI Interiors', label: 'AI Interiors', icon: 'frame' },
      // { id: 'Tutorials', label: 'Tutorials', icon: 'lightbulb' },
    ];
    if (isSupplier) {
      items.splice(2, 0, { id: 'Supplier', label: 'Supplier', icon: 'sofa' });
    }
    return items;
  }, [isSupplier]);

  const handleStartDesigning = async () => {
    // Do NOT create a room until the user uploads an image.
    setRoomId(null);
    setRoomImageId(null);
    setPhotorealisticImage(null);
    setCurrentView('StartDesigning');
    setActiveItem('StartDesigning');
  };

  // Don't render sidebar on Photorealistic page (full-width content)
  if (currentView === 'Photorealistic') {
    return null;
  }

  const getIcon = (iconName, isSolid = false) => {
    const icons = {
      grid: (
        isSolid ? (
          /*<svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <rect x="4" y="4" width="7" height="7" rx="1" /> <rect x="4" y="13" width="7" height="7" rx="1" /> <rect x="13" y="4" width="7" height="7" rx="1" /><rect x="13" y="13" width="7" height="7" rx="1" />
          </svg>*/
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        ) : (
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        )
      ),
      frame: (
        isSolid ? (
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18 3H6a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V6a3 3 0 00-3-3zM8 7a1.5 1.5 0 11-1.5 1.5A1.5 1.5 0 018 7zm11 10.83A1.09 1.09 0 0118 19H6l7.57-6.82a.69.69 0 01.93 0l4.5 4.48z" />
          </svg>
        ) : (
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 3H6a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V6a3 3 0 00-3-3zM6 5h12a1 1 0 011 1v8.36l-3.2-2.73a2.77 2.77 0 00-3.52 0L5 17.7V6a1 1 0 011-1z" />
            <circle cx="8" cy="8.5" r="1.5" />
          </svg>
        )
      ),
      sofa: (
        isSolid ? (
          /*<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 3s-6.186 5.34-9.643 8.232A1 1 0 002 12c0 .553.447 1 1 1h2v7a1 1 0 001 1h3a1 1 0 001-1v-4h4v4a1 1 0 001 1h3a1 1 0 001-1v-7h2a1 1 0 001-1c0-.316-.154-.584-.383-.768C18.184 8.34 12 3 12 3z" />
          </svg>*/
          
          <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" >
            <path d="M7 17v2M17 17v2" />
            <path d="M3 9c0-.8.9-1.2 1.8-1.2S6.6 8.2 6.6 9c0 1.4.3 3 2.1 3h6.6c1.8 0 2.1-1.6 2.1-3 0-.8.9-1.2 1.8-1.2S21 8.2 21 9v4c0 1.7-1.2 3-2.7 3H5.7C4.2 16 3 14.7 3 13V9z" />
            <path d="M5 9V5c0-1.7 1.2-3 2.7-3h8.6C17.8 2 19 3.3 19 5v4" />
          </svg>

        ) : (

          /*<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>*/

        <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" >
          <path d="M7 17v2M17 17v2" />
          <path d="M3 9c0-.8.9-1.2 1.8-1.2S6.6 8.2 6.6 9c0 1.4.3 3 2.1 3h6.6c1.8 0 2.1-1.6 2.1-3 0-.8.9-1.2 1.8-1.2S21 8.2 21 9v4c0 1.7-1.2 3-2.7 3H5.7C4.2 16 3 14.7 3 13V9z" />
          <path d="M5 9V5c0-1.7 1.2-3 2.7-3h8.6C17.8 2 19 3.3 19 5v4" />
        </svg>

        )
      ),
      ruler: (
        isSolid ? (
          <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" >
            <path d="M7 9l5 2.5M7 9v5l5 2.5M7 9l5-2.5 5 2.5M12 11.5l5-2.5M12 11.5v5M12 16.5l5-2.5V9" />
            <path d="M3 15c0 2.2 0 3.3.4 4.1.35.7.9 1.25 1.6 1.6.8.4 1.9.4 4.1.4M21 15c0 2.2 0 3.3-.4 4.1-.35.7-.9 1.25-1.6 1.6-.8.4-1.9.4-4.1.4M15 3c2.2 0 3.3 0 4.1.4.7.35 1.25.9 1.6 1.6.4.8.4 1.9.4 4.1M3 9c0-2.2 0-3.3.4-4.1.35-.7.9-1.25 1.6-1.6C6 3 7.1 3 9.3 3" />
          </svg>

          /*<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2l4 4h-4V2zM14 2H5a1 1 0 00-1 1v18a1 1 0 001 1h14a1 1 0 001-1V8h-6z" />
          </svg>*/
        ) : (
          /*<svg className="w-5 h-5" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M13 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V9M13 3l6 6M13 3v5a1 1 0 001 1h5" />
          </svg>*/

          <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" >
            <path d="M7 9l5 2.5M7 9v5l5 2.5M7 9l5-2.5 5 2.5M12 11.5l5-2.5M12 11.5v5M12 16.5l5-2.5V9" />
            <path d="M3 15c0 2.2 0 3.3.4 4.1.35.7.9 1.25 1.6 1.6.8.4 1.9.4 4.1.4M21 15c0 2.2 0 3.3-.4 4.1-.35.7-.9 1.25-1.6 1.6-.8.4-1.9.4-4.1.4M15 3c2.2 0 3.3 0 4.1.4.7.35 1.25.9 1.6 1.6.4.8.4 1.9.4 4.1M3 9c0-2.2 0-3.3.4-4.1.35-.7.9-1.25 1.6-1.6C6 3 7.1 3 9.3 3" />
          </svg>
        )
      ),
      lightbulb: (
        isSolid ? (
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M12 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm7.7071 2.2929a1 1 0 010 1.4142l-1 1a1 1 0 11-1.4142-1.4142l1-1a1 1 0 011.4142 0zM19 12a1 1 0 011-1h1a1 1 0 110 2h-1a1 1 0 01-1-1zM2 12a1 1 0 011-1h1a1 1 0 110 2H3a1 1 0 01-1-1zm2.2929-7.7071a1 1 0 011.4142 0l1 1A1 1 0 015.2929 6.7071l-1-1a1 1 0 010-1.4142zM12 6a6 6 0 00-6 6c0 1.6332.6539 3.1157 1.7119 4.1966.2678.2737.4122.5251.4568.7478l.5291 2.6442A2 2 0 0011.6395 22h.721A2 2 0 0015.3022 19.5886l.5291-2.6442c.0446-.2227.189-.4741.4568-.7478C17.3461 15.1157 18 13.6332 18 12a6 6 0 00-6-6zm-1 10a1 1 0 100 2h2a1 1 0 100-2h-2z"/>
          </svg>
        ) : (
          <svg className="w-7 h-7" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 7a5 5 0 00-5 5c0 1.3613.544 2.5955 1.4265 3.4972.3438.3512.6263.7691.7227 1.251l.5291 2.6442A1.75 1.75 0 0011.6395 21h.721A1.75 1.75 0 0014.3217 19.3924l.5291-2.6442c.0964-.4819.3789-.8998.7227-1.251A5 5 0 0017 12a5 5 0 00-5-5z" />
            <path d="M12 4V3" />
            <path d="M18 6l1-1" />
            <path d="M20 12h1" />
            <path d="M4 12H3" />
            <path d="M5 5l1 1" />
            <path d="M10 17h4" />
          </svg>
        )
      ),
      settings: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      help: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  const isStartDesignActive = currentView === 'StartDesigning';

  return (
    <aside className="sidebar-gradient-overlay h-[calc(100vh-4rem)] z-40 w-[88px] bg-white transition-all duration-300">
      <div className="flex flex-col h-full min-w-0">
        {/* Start Designing — same icon-pill hover/active as nav */}
        <div className="px-1 pt-6 pb-0">
          <button
            type="button"
            onClick={handleStartDesigning}
            disabled={isCreatingRoom}
            title="Start Designing"
            aria-label="Start Designing"
            className="group w-full flex flex-col items-center gap-2 py-2 px-0.5 rounded-xl transition-colors text-gray-700 disabled:opacity-60"
          >
            <span
              className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${
                isStartDesignActive
                  ? 'bg-primary-300 text-white'
                  : 'text-gray-600 group-hover:bg-gray-100 group-hover:text-gray-900 group-focus-visible:bg-gray-100 group-focus-visible:text-gray-900'
              }`}
            >
              <svg className="w-8 h-8 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden>
                <path d="M12 12H8M12 12H16M12 12V8M12 12V16" />
                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <span
              className={`${narrowLabelClass} ${isStartDesignActive ? 'text-gray-900' : 'text-gray-600'}`}
            >
              {isCreatingRoom ? 'Creating…' : 'Start Design'}
            </span>
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-1 py-2 min-w-0">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = activeItem === item.id && (currentView === 'Dashboard' || currentView === item.id);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveItem(item.id);
                      setCurrentView(item.id);
                    }}
                    onMouseEnter={() => setHoveredNavItem(item.id)}
                    onMouseLeave={() => setHoveredNavItem(null)}
                    title={item.label}
                    aria-label={item.label}
                    className="group w-full flex flex-col items-center gap-1 py-2 px-0.5 rounded-xl transition-colors text-gray-700"
                  >
                    <span
                      className={`inline-flex items-center justify-center rounded-full p-1.5 transition-colors ${
                        isActive
                          ? 'bg-primary-300 text-white'
                          : 'text-gray-600 group-hover:bg-primary-300 group-hover:text-white group-focus-visible:bg-primary-300 group-focus-visible:text-white'
                      }`}
                    >
                      {getIcon(item.icon, isActive || hoveredNavItem === item.id)}
                    </span>
                    <span
                      className={`${narrowLabelClass} ${isActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}
                    >
                      {item.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Menu Items */}
        <div className="px-1 py-2 space-y-1 min-w-0">
          <button
            type="button"
            title="Help"
            aria-label="Help"
            className="group w-full flex flex-col items-center gap-1 py-2 px-0.5 rounded-lg text-gray-700 transition-colors opacity-50 pointer-events-none"
          >
            <span className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-600">
              {getIcon('help')}
            </span>
            <span className={narrowLabelClass}>Help</span>
          </button>
          <button
            type="button"
            title="Settings"
            aria-label="Settings"
            className="group w-full flex flex-col items-center gap-1 py-2 px-0.5 rounded-lg text-gray-700 transition-colors opacity-50 pointer-events-none"
          >
            <span className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-600">
              {getIcon('settings')}
            </span>
            <span className={narrowLabelClass}>Settings</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
